import axios from "axios";

export const BILLING_STARTER_PACK_DEFINITIONS = [
  { code: "Decoration", aliases: ["decoration"] },
  { code: "Foldbag", aliases: ["foldbag", "fold bag"] },
  { code: "Freight", aliases: ["freight"] },
  { code: "LessMin", aliases: ["lessmin", "less min", "less minimum"] },
  { code: "Service", aliases: ["service", "sample"] },
  { code: "Proof", aliases: ["proof", "paperproof", "paper proof"] },
  { code: "PMS Color", aliases: ["pmscolor", "pms color", "pms colour"] },
  { code: "Rush", aliases: ["rush"] },
  { code: "Setup", aliases: ["setup", "set up"] },
  { code: "Shipping", aliases: ["shipping"] },
  { code: "ProcessingFee", aliases: ["processingfee", "processing fee"] },
  { code: "Duty", aliases: ["duty"] },
  { code: "US Tax", aliases: ["ustax", "us tax"] },
  { code: "Other Fee", aliases: ["otherfee", "other fee"] },
];
export const BILLING_STARTER_PACK_CODES = BILLING_STARTER_PACK_DEFINITIONS.map((row) => row.code);
export const INVOICE_FINANCE_BUCKETS = [
  {
    key: "product_breakdown",
    label: "Product Breakdown / SKU",
    description: "Product pricing rows where Type is BreakDown / Breakdown.",
  },
  {
    key: "product_run_charge",
    label: "Product Run Charge",
    description: "Product pricing rows where Type is Cost and SubType is Run Charge.",
  },
  {
    key: "product_fixed_charge",
    label: "Product Fixed Charge",
    description: "Product pricing rows where Type is Cost and SubType is blank/null/non-run/non-international.",
  },
  {
    key: "inbound_freight",
    label: "Inbound Freight",
    description: "International pricing rows where SubType is Inbound freight.",
  },
  {
    key: "brokerage",
    label: "Brokerage",
    description: "International pricing rows where SubType is Brokerage.",
  },
  {
    key: "duty",
    label: "Duty",
    description: "International pricing rows where SubType is Duty.",
  },
  {
    key: "decoration",
    label: "Decoration",
    description: "Decoration charge rows from decorations[].Charge[].",
  },
  {
    key: "art",
    label: "Art",
    description: "OtherService rows where Type is Art, and later product-attached artwork rows if export splitting supports them.",
  },
  {
    key: "shipping",
    label: "Shipping",
    description: "OtherService rows where Type is Shipping.",
  },
  {
    key: "other_service",
    label: "Other Service",
    description: "OtherService rows where Type is Other.",
  },
];
export const INVOICE_FINANCE_BUCKET_KEYS = INVOICE_FINANCE_BUCKETS.map((bucket) => bucket.key);

const VALID_SCOPES = new Set(["all", "bills", "invoices"]);
const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
const authHeaders = () => ({ headers: { accessToken: localStorage.getItem("accessToken") } });
const trimString = (value) => String(value ?? "").trim();
export function normalizeFinanceCodeKey(value = "") {
  return trimString(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
}

const codeKey = normalizeFinanceCodeKey;
const nowIso = () => new Date().toISOString();
const generateId = () => (typeof crypto?.randomUUID === "function"
  ? crypto.randomUUID()
  : `finance-code-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

export function normalizeFinanceCodeAliases(value) {
  const rawAliases = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(/[\n,]+/g)
      : [];

  const seen = new Set();
  const aliases = [];
  rawAliases.forEach((alias) => {
    const trimmed = trimString(alias);
    const normalizedKey = normalizeFinanceCodeKey(trimmed);
    if (!trimmed || !normalizedKey || seen.has(normalizedKey)) return;
    seen.add(normalizedKey);
    aliases.push(trimmed);
  });
  return aliases;
}

export function normalizeInvoiceBuckets(value = []) {
  const allowed = new Set(INVOICE_FINANCE_BUCKET_KEYS);
  const rawBuckets = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(/[\n,]+/g)
      : [];

  const seen = new Set();
  const buckets = [];
  rawBuckets.forEach((bucket) => {
    const trimmed = trimString(bucket);
    if (!trimmed || !allowed.has(trimmed) || seen.has(trimmed)) return;
    seen.add(trimmed);
    buckets.push(trimmed);
  });
  return buckets;
}

export function isInvoiceFinanceCodeEligible(row = {}) {
  const enabled = row?.enabled !== false;
  const appliesToInvoices = row?.appliesToInvoices === true || row?.appliesToInvoice === true;
  return enabled && appliesToInvoices && normalizeInvoiceBuckets(row?.invoiceBuckets).length > 0;
}

export function getInvoiceFinanceCodeMappingState(row = {}) {
  const normalizedBuckets = normalizeInvoiceBuckets(row?.invoiceBuckets);
  const hasInvoiceToggle = row?.appliesToInvoices === true || row?.appliesToInvoice === true;
  const eligible = isInvoiceFinanceCodeEligible(row);

  if (eligible) {
    return {
      eligible: true,
      disabled: false,
      helperText: "",
      normalizedBuckets,
    };
  }

  if (!hasInvoiceToggle) {
    return {
      eligible: false,
      disabled: true,
      helperText: "Enable Applies to invoice and assign invoice buckets in Defaults > Finance Codes before mapping this code to a QBO invoice item.",
      normalizedBuckets,
    };
  }

  if (!normalizedBuckets.length) {
    return {
      eligible: false,
      disabled: true,
      helperText: "Assign one or more invoice buckets in Defaults > Finance Codes before mapping this code to a QBO invoice item.",
      normalizedBuckets,
    };
  }

  if (row?.enabled === false) {
    return {
      eligible: false,
      disabled: true,
      helperText: "Enable this finance code in Defaults > Finance Codes before mapping it to a QBO invoice item.",
      normalizedBuckets,
    };
  }

  return {
    eligible: false,
    disabled: true,
    helperText: "Enable Applies to invoice and assign invoice buckets in Defaults > Finance Codes before mapping this code to a QBO invoice item.",
    normalizedBuckets,
  };
}

export function normalizeFinanceCodeScope(scope = "all") {
  const normalized = trimString(scope).toLowerCase();
  return VALID_SCOPES.has(normalized) ? normalized : "all";
}

export function buildBillingStarterPackCodes() {
  const createdAt = nowIso();
  return BILLING_STARTER_PACK_DEFINITIONS.map(({ code, aliases }, index) => ({
    id: generateId(),
    code,
    label: code,
    description: "",
    enabled: true,
    appliesToBills: true,
    appliesToInvoices: false,
    sortOrder: (index + 1) * 10,
    aliases: normalizeFinanceCodeAliases(aliases),
    createdAt,
    updatedAt: createdAt,
  }));
}

export function normalizeFinanceCodes(financeCodes = []) {
  if (!Array.isArray(financeCodes) || !financeCodes.length) return [];
  return [...financeCodes]
    .map((row, index) => {
      const code = trimString(row?.code || "");
      if (!code) return null;
      const timestamp = trimString(row?.updatedAt || row?.createdAt || "") || nowIso();
      return {
        id: trimString(row?.id || "") || generateId(),
        code,
        label: trimString(row?.label || code),
        description: trimString(row?.description || ""),
        enabled: row?.enabled !== false,
        appliesToBills: row?.appliesToBills !== false,
        appliesToInvoices: row?.appliesToInvoices === true || row?.appliesToInvoice === true,
        sortOrder: Number.isFinite(Number(row?.sortOrder)) ? Number(row.sortOrder) : (index + 1) * 10,
        aliases: normalizeFinanceCodeAliases(row?.aliases),
        invoiceBuckets: normalizeInvoiceBuckets(row?.invoiceBuckets),
        qboInvoiceItemRef: trimString(row?.qboInvoiceItemRef || ""),
        qboInvoiceItemName: trimString(row?.qboInvoiceItemName || ""),
        qboInvoiceItemType: trimString(row?.qboInvoiceItemType || ""),
        qboBillAccountRef: trimString(row?.qboBillAccountRef || ""),
        qboBillAccountName: trimString(row?.qboBillAccountName || ""),
        qboBillAccountType: trimString(row?.qboBillAccountType || ""),
        qboBillAccountSubType: trimString(row?.qboBillAccountSubType || ""),
        createdAt: trimString(row?.createdAt || "") || timestamp,
        updatedAt: timestamp,
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      const sortDelta = Number(left.sortOrder || 0) - Number(right.sortOrder || 0);
      if (sortDelta !== 0) return sortDelta;
      return trimString(left.label || left.code).localeCompare(trimString(right.label || right.code));
    });
}

export function classifyInvoicePricingBucket(row = {}) {
  const type = trimString(row?.Type || row?.type || "").toLowerCase();
  const subType = trimString(row?.SubType || row?.subType || "").toLowerCase();
  const costName = trimString(row?.CostName || row?.costName || "").toLowerCase();

  if (type === "breakdown") return "product_breakdown";
  if (type === "cost" && subType === "run charge") return "product_run_charge";
  if (type === "cost" && (!subType || subType === "fixed charge")) return "product_fixed_charge";
  if (subType === "inbound freight") return "inbound_freight";
  if (subType === "brokerage") return "brokerage";
  if (subType === "duty") return "duty";
  if (costName === "inbound freight") return "inbound_freight";
  if (costName === "brokerage") return "brokerage";
  if (costName === "duty") return "duty";
  return "product_breakdown";
}

export function filterFinanceCodesByScope(financeCodes = [], scope = "all") {
  const normalized = normalizeFinanceCodeScope(scope);
  const rows = normalizeFinanceCodes(financeCodes);
  if (normalized === "all") return rows;
  return rows.filter((row) => {
    if (!row.enabled) return false;
    if (normalized === "bills") return row.appliesToBills !== false;
    if (normalized === "invoices") return row.appliesToInvoices === true;
    return true;
  });
}

export function resolveFinanceCodeFromAlias(rawValue, financeCodes = [], scope = "all") {
  const normalizedKey = normalizeFinanceCodeKey(rawValue);
  if (!normalizedKey) return null;

  const rows = filterFinanceCodesByScope(financeCodes, scope);
  return rows.find((row) => {
    if (normalizeFinanceCodeKey(row?.code) === normalizedKey) return true;
    if (normalizeFinanceCodeKey(row?.label) === normalizedKey) return true;
    return normalizeFinanceCodeAliases(row?.aliases).some((alias) => normalizeFinanceCodeKey(alias) === normalizedKey);
  }) || null;
}

export function buildFinanceCodeDropdownOptions(financeCodes = [], selectedCode = "") {
  const normalizedCodes = filterFinanceCodesByScope(financeCodes, "bills");
  const options = normalizedCodes.map((row) => ({
    id: row.code,
    name: row.label || row.code,
  }));

  const value = trimString(selectedCode);
  if (!value) return options;
  const existing = options.some((option) => option.id === value);
  if (existing) return options;
  return [
    ...options,
    { id: value, name: `${value} (Legacy)` },
  ];
}

export function hasDuplicateFinanceCode(financeCodes = [], code = "", currentId = "") {
  const key = codeKey(code);
  if (!key) return false;
  return normalizeFinanceCodes(financeCodes).some((row) => codeKey(row.code) === key && String(row.id) !== String(currentId));
}

export function hasAnyDuplicateFinanceCodes(financeCodes = []) {
  const normalized = normalizeFinanceCodes(financeCodes);
  return normalized.some((row) => hasDuplicateFinanceCode(normalized, row.code, row.id));
}

export function hasEnabledBillFinanceCodes(financeCodes = []) {
  return filterFinanceCodesByScope(financeCodes, "bills").length > 0;
}

async function resolveInstanceId(instanceUniqueId) {
  const response = await axios.get(`${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`, authHeaders());
  return response.data?.id || null;
}

export async function fetchFinanceCodes(instanceUniqueId, scope = "all") {
  if (!instanceUniqueId) return [];
  const instanceId = await resolveInstanceId(instanceUniqueId);
  if (!instanceId) return [];
  const normalizedScope = normalizeFinanceCodeScope(scope);
  const response = await axios.get(
    `${SERVER}/instances/${instanceId}/finance-codes`,
    { ...authHeaders(), params: { scope: normalizedScope } }
  );
  return normalizedScope === "all"
    ? normalizeFinanceCodes(response.data?.financeCodes || [])
    : filterFinanceCodesByScope(response.data?.financeCodes || [], normalizedScope);
}

export async function fetchBillFinanceCodes(instanceUniqueId) {
  return fetchFinanceCodes(instanceUniqueId, "bills");
}

export async function fetchInvoiceFinanceCodes(instanceUniqueId) {
  return fetchFinanceCodes(instanceUniqueId, "invoices");
}

export async function saveFinanceCodes(instanceUniqueId, financeCodes = []) {
  const instanceId = await resolveInstanceId(instanceUniqueId);
  const response = await axios.put(
    `${SERVER}/instances/${instanceId}/defaults`,
    { financeCodes: normalizeFinanceCodes(financeCodes) },
    authHeaders()
  );
  return normalizeFinanceCodes(response.data?.defaults?.financeCodes || []);
}

export async function installFinanceCodeStarterPack(instanceUniqueId, packName = "billing") {
  const instanceId = await resolveInstanceId(instanceUniqueId);
  const response = await axios.post(
    `${SERVER}/instances/${instanceId}/finance-codes/install-starter-pack`,
    { packName },
    authHeaders()
  );
  return {
    added: Number(response.data?.added || 0),
    skipped: Number(response.data?.skipped || 0),
    financeCodes: normalizeFinanceCodes(response.data?.financeCodes || []),
  };
}
